import SubjectIdentityBanner from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectIdentityBanner';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import describeArchivedDossier from '@UI/Pages/Beneficiaries/Shared/Helpers/describeArchivedDossier';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { css } from '@emotion/react';
import buildSubjectInitials from '@UI/Pages/Subjects/Helpers/buildSubjectInitials';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import type SubjectView from '@UI/Store/Subjects/Views/SubjectView';
import * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const FALLBACK_TITLE = 'Dossier';

const archivedBannerStyle = css`
	padding: 0.875rem 1.125rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
`;

type Props = {
	readonly subject: SubjectView;
	readonly type: SubjectTypeView;
	readonly facilityName: string;
	/** Actions drawn inside the banner, under the identity. */
	readonly children?: React.ReactNode;
};

/** The identity banner of a dossier of a configurable type: initials, title, the type's singular as a tag, agency and creation date. */
const SubjectHeader: React.FC<Props> = ({ subject, type, facilityName, children }) => {
	return (
		<React.Fragment>
			<SubjectIdentityBanner
				initials={buildSubjectInitials(subject.title)}
				seed={subject.id}
				title={subject.title ?? FALLBACK_TITLE}
				tags={
					<React.Fragment>
						<Tag value={type.wording.singular} severity="secondary" rounded />
						<ConditionalLoader data={subject.isArchived}>
							<Tag value="Archivé" severity="secondary" rounded />
						</ConditionalLoader>
					</React.Fragment>
				}
				metadata={
					<li>
						{facilityName} · Créé le {formatLongDate(subject.createdAt.toISOString())}
					</li>
				}
			>
				{children}
			</SubjectIdentityBanner>

			<ConditionalLoader data={subject.isArchived}>
				<p css={archivedBannerStyle}>
					{describeArchivedDossier(subject.isPurgeApproaching, subject.purgeScheduledAt)}
				</p>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default SubjectHeader;
